import TimelineComponent from './components/timelineComponent';
import {AgyleTable, createAgyleTableColumn} from '../../components/table/AgyleTable';
import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import makeStyles from '@mui/styles/makeStyles';
import AgyleLoadingSpinner from '../../components/other/agyleLoadingSpinner';
import {Box, Divider, FormLabel, Paper} from '@mui/material';
import _ from 'lodash';
import moment from 'moment';
import config from '../../app/config';
import ApiReports from '../../app/apiReports';
import {setSnackbar} from '../../store/reducers/globals';
import IconButton from '@mui/material/IconButton';
import DescriptionIcon from '@mui/icons-material/Description';
import {AgyleTab, AgyleTabs} from '../../components/ui/tabs/agyleTabs';
import {loadNotesForPatient, timelineNotes} from '../../store/reducers/notes';
import useOpenState from '../../hooks/useOpenState';
import AgyleDialog from '../../components/modal/agyleDialog';
import SingleNoteComponent from '../../components/notes/singleNote';
import useDict from '../../hooks/useDict';
import AgyleButton from '../../components/ui/buttons/agyleButton';
import Grid from '@mui/material/Grid';
import {TimerCountUp} from '../../controls/agyle/timer';
import {UtilsMomentJS} from '../../store/utilsMoment';
import {calculateArrivalDate} from '../../util/patientUtil';
import {ambulanceCodes} from '../../store/reducers/dicts';
import {formNameException} from '../../components/notes/notesUtil';

const useStyles = makeStyles((theme) => ({
    root: {
        padding: 5,
    },
    paper: {
        padding: 10,
        backgroundColor: theme.palette.background.default,
        borderRadius: 6,
        marginBottom: 10,

        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    timeMarker: {
        backgroundColor: theme.palette.background.field,
        borderRadius: '7px',
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
        alignItems: 'center',
        padding: '4px 10px',
        marginBottom: 5,
    },
    timeMarkerText: {
        marginLeft: 10,
        fontWeight: 'bold',
    },
    table: {
        maxHeight: 'calc(100vh - 370px)',
    },
    modalBackground: {
        backgroundColor: theme.palette.mode === 'light' ? '#ffffff' : '#313233',
    },
    text: {
        fontWeight: 'bold',
        fontSize: 13,
    },
    timerDynamic: {
        '& .timer-count-up': {
            userSelect: 'none',
            padding: '2px 4px',
            borderRadius: 5,
            fontWeight: 'bold',
            textAlign: 'center',
        },
    },
    button: {
        color: theme.palette.primary.main
    }
}));

const TABLE_COLUMNS = [
    createAgyleTableColumn('type', 'Action Type', 1, 'type', 'clinicalNotesNames', '', 'medium', 'left', true, true, null, null),
    createAgyleTableColumn('date', 'Timestamp', 2, 'date', 'date', '', 'medium', 'left', true, true, null, null),
    createAgyleTableColumn('clinician', 'Clinician Set', 3, 'clinician_set', 'string', '', 'medium', 'left', true, true, null, null),
    createAgyleTableColumn('dateSet', 'Datetime Set', 4, 'date_set', 'string', '', 'medium', 'left', true, true, null, null),
    createAgyleTableColumn('action', 'Action', 5, null, 'action', 'notSort', 'medium', 'right', true, true, null, null),
];

const PatientTimeline = ({patient}) => {
    const {t} = useTranslation();
    const classes = useStyles();
    const dispatch = useDispatch();

    const ambulance = useSelector(ambulanceCodes);

    const controller = new AbortController();
    const startDate = moment(patient?.admission?.startDate);
    const endDate = patient?.admission?.endDate ? moment(patient?.admission?.endDate) : moment();

    // Timelines
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);

    const [currentTab, setCurrentTab] = React.useState('table');

    // Notes
    const notes = useSelector(timelineNotes);
    const [clinicalNoteType] = useDict('ClinicalNoteType');
    const [isNoteOpen, openNote, closeNote] = useOpenState(false, true);
    const [selectedNote, setSelectedNote] = useState(null);

    // Expand more clinicians
    const [isExpandCliniciansOpen, openExpandClinicians, closeExpandClinicians] = useOpenState(false, true);
    const [tempObject, setTempObject] = useState(null);

    useEffect(() => {
        setLoading(true);
        dispatch(loadNotesForPatient(patient, controller.signal, true, true));

        ApiReports.getPatientTimeline(
                {
                    fromDate: startDate,
                    toDate: endDate,
                    piwId: patient?.__id,
                },
                controller.signal)
            .then(res => {
                prepareData(res);
            })
            .catch(error => {
                console.error(error);
                dispatch(setSnackbar({
                    type: 'error',
                    message: '[Reports API] An error occurred while retrieving the patient\'s timeline.',
                }));
            })
            .finally(() => {
                setLoading(false);
            });
        return () => {
            controller.abort();
        };
    }, [patient]);

    const handleCloseNote = () => {
        closeNote();
        setSelectedNote(null);
    };

    const handleCloseExpandClinicians = () => {
        closeExpandClinicians();
        setTempObject(null);
    };

    const prepareData = (timeline) => {
        const tableData = [];
        _.forEach(timeline, tl => {
            let obj = {...tl};
            if (obj?.date_set) {
                obj.date_set = moment(obj.date_set).format(config.defaultDateTimeFormat);
            }
            if (_.isObject(obj.clinician_set)) {
                obj.clinician_set = obj.clinician_set?.__description;
            }
            if (tl.clinicians_list) {
                obj.clinician_set = (
                    <AgyleButton
                        size="small"
                        colour="primary"
                        onClick={handleExpandClinicians.bind(this, tl)}
                        className={classes.button}
                    >
                        {t('More')}
                    </AgyleButton>
                );
                obj.date_set = (
                    <AgyleButton
                        size="small"
                        colour="primary"
                        onClick={handleExpandClinicians.bind(this, tl)}
                        className={classes.button}
                    >
                        {t('More')}
                    </AgyleButton>
                );
            }
            tableData.push(obj);
        });

        setData(tableData);
    };

    const handleExpandClinicians = (obj) => {
        if (obj) {
            setTempObject(obj);
            openExpandClinicians();
        }
    };

    const handleOpenNote = (noteId) => {
        const findNote = _.find(notes, {__id: noteId});
        if (findNote) {
            setSelectedNote(findNote);
            openNote();
        } else {
            dispatch(setSnackbar({type: 'warning', message: t('Cannot find the clinical note.')}));
        }
    };

    const handleTabChange = (event, newTabValue) => {
        setCurrentTab(newTabValue);
    };

    const renderCustomButtons = (obj) => {
        if (!obj?.note_id) return null;

        return (
            <IconButton
                size="small"
                onClick={handleOpenNote.bind(this, obj?.note_id)}
            >
                <DescriptionIcon fontSize="small"/>
            </IconButton>
        );
    };

    const renderKeyMarkers = () => {
        const getData = (marker) => {
            const val = _.get(patient?.metadata, marker);
            if (val) {
                return `${_.isString(val?.who) ? val?.who : val?.who?.__description} at ${moment(val?.when).format(config.defaultDateTimeFormat)}`;
            } else {
                // eslint-disable-next-line default-case
                switch (marker) {
                    case 'timeInED':
                        return (
                            <div className={classes.timerDynamic}>
                                <TimerCountUp
                                    renderWithBG
                                    limit={240}
                                    upperLimit={-1}
                                    countDownSince={
                                        patient.admission?.status === 'DISCHARGE_REQUESTED' ||
                                        patient.admission?.status === 'TRANSFER_REQUESTED' ? checkStatus() : false
                                    }
                                    time={getPatientTime()}
                                    limitMessage={t('4hr Target Breached!')}
                                />
                            </div>
                        );
                    case 'departureDate':
                        let date;
                        if (patient?.metadata?.dischargeTimeMarker) {
                            date = patient?.metadata?.dischargeTimeMarker;
                        } else if (patient?.studies?.discharge) {
                            date = UtilsMomentJS.getLocalDateFromUTCECDSFormat(patient?.studies?.discharge.departureDate, patient?.studies?.discharge.departureTime);
                        } else if (patient.admission?.endDate) {
                            date = patient.admission?.endDate;
                        }

                        if (date) return moment(date).format(config.defaultDateTimeFormat);
                        break;
                    case 'decisionToAdmit':
                        let dateToAdmit;
                        if (patient?.csmContactedTime?.activityTreatment) {
                            dateToAdmit = patient?.csmContactedTime?.activityTreatment;
                        } else if (patient?.studies?.bedRequest) {
                            dateToAdmit = patient?.studies?.bedRequest?.requestedWhen || null;
                        }

                        if (patient?.facility) {
                            dateToAdmit = checkLastObjectForDecidedToAdmit(dateToAdmit, patient?.diagnosis?.primary, 'procedureTime', 'procedureDate');
                            dateToAdmit = checkLastObjectForDecidedToAdmit(dateToAdmit, patient?.studies?.referrals?.ReferralsToOtherServices, 'ActivityServiceRequestTime_EmergencyCare', 'ActivityServiceRequestDate_EmergencyCare');
                        }

                        if (dateToAdmit) return moment(dateToAdmit).format(config.defaultDateTimeFormat);
                        break;
                }
            }
            return t('Not Set');
        };

        return (
            <Paper className={classes.paper}>
                <Grid item xs={12} sm={6}>
                    <div className={classes.timeMarker}>
                        <FormLabel>
                            {t('Initial Assessment')}:
                        </FormLabel>

                        <div className={classes.timeMarkerText}>
                            {getData('firstInitialAssessment')}
                        </div>
                    </div>

                    <div className={classes.timeMarker}>
                        <FormLabel>
                            {t('Time Seen For Treatment')}:
                        </FormLabel>

                        <div className={classes.timeMarkerText}>
                            {getData('firstClinicianSaved')}
                        </div>
                    </div>

                    <div className={classes.timeMarker}>
                        <FormLabel>
                            {t('Decision to Admit')}:
                        </FormLabel>

                        <div className={classes.timeMarkerText}>
                            {getData('decisionToAdmit')}
                        </div>
                    </div>
                </Grid>

                <Grid item xs={12} sm={6}>
                    <div className={classes.timeMarker}>
                        <FormLabel>
                            {t('Ready to proceed')}:
                        </FormLabel>

                        <div className={classes.timeMarkerText}>
                            {getData('firstClinicallyReadyToProceedSaved')}
                        </div>
                    </div>

                    <div className={classes.timeMarker}>
                        <FormLabel>
                            {t('Departure Date')}:
                        </FormLabel>

                        <div className={classes.timeMarkerText}>
                            {getData('departureDate')}
                        </div>
                    </div>

                    <div className={classes.timeMarker}>
                        <FormLabel>
                            {t('Time in Department')}:
                        </FormLabel>

                        <div className={classes.timeMarkerText}>
                            {getData('timeInED')}
                        </div>
                    </div>
                </Grid>
            </Paper>
        );
    };

    const checkLastObjectForDecidedToAdmit = (currentDate, array, timeFiled, dateField) => {
        if (array && !_.isEmpty(array)) {
            const formatDate = array.map((item) => ({
                ...item,
                datetimeFormatted: UtilsMomentJS.getLocalDateFromUTCECDSFormat(item[dateField], item[timeFiled]),
            }));
            const sortArray = _.sortBy(formatDate, 'datetimeFormatted');
            const firstObject = sortArray[0];
            if (firstObject?.datetimeFormatted && (currentDate && moment(currentDate).isAfter(firstObject.datetimeFormatted))) {
                return firstObject.datetimeFormatted;
            }
        }
        return currentDate;
    };

    const getPatientTime = (format) => {
        // If patient have arrival mode code for ambulance then calculate time form ambulance handover
        if (!_.isEmpty(ambulance) && ambulance.includes(patient.admission?.arrivalModeCode)) {
            return format ? moment(calculateArrivalDate(patient)).format(config.defaultDateTimeFormat) : calculateArrivalDate(patient);
        } else {
            return format ? moment(patient?.admission?.startDate).format(config.defaultDateTimeFormat) : moment(patient?.admission?.startDate);
        }
    };

    const checkStatus = () => {
        let dischargeDate;
        if (patient?.studies?.discharge) {
            const date = moment(patient?.studies?.discharge?.departureDate, config.ecdsDateFormat);
            const time = moment(patient?.studies?.discharge?.departureTime).set(
                {
                    year: date.get('year'),
                    month: date.get('month'),
                    day: date.get('day'),
                },
            ).toISOString();
            if (!moment(patient?.studies?.discharge?.departureTime).isValid()) {
                dischargeDate = UtilsMomentJS.getLocalDateFromUTCECDSFormat(patient?.studies?.discharge?.departureDate, patient?.studies?.discharge?.departureTime);
            } else {
                dischargeDate = time;
            }
        }
        return dischargeDate;
    };

    const renderRoot = () => {
        return (
            <>
                {renderKeyMarkers()}
                <AgyleTabs
                    value={currentTab}
                    variant={'scrollable'}
                    onChange={handleTabChange}
                >
                    <AgyleTab
                        label={t('Table')}
                        value="table"
                    />
                    <AgyleTab
                        label={t('Timeline')}
                        value="timeline"
                    />
                </AgyleTabs>

                <TabPanel value={currentTab} index="timeline">
                    <TimelineComponent
                        events={data}
                        startTime={startDate}
                        endTime={endDate}
                        discharged={!_.isNil(patient?.admission?.endDate)}
                    />
                </TabPanel>

                <TabPanel value={currentTab} index="table">
                    <AgyleTable
                        data={data}
                        columns={TABLE_COLUMNS}
                        edRefresh
                        customActionItem={renderCustomButtons}
                        minHeightRowCell={35}
                        tableContainerClass={classes.table}
                        noMaxHeight
                    />
                </TabPanel>
            </>
        );
    };

    const renderExpandClinicians = () => {
        if (!tempObject) return;

        return (
            <>
                {tempObject?.clinicians_list.map((i, index) => {
                    const name = Object.keys(i)[0];
                    const clinician = _.isString(i[name]?.clinician) ? i[name]?.clinician : i[name]?.clinician?.__description;

                    return (
                        <div key={index}>
                            {index > 0 &&
                                <Divider style={{margin: 10}}/>
                            }

                            <FormLabel>
                                {getNoteName(name)}
                            </FormLabel>

                            <div className={classes.text}>
                                <div>{clinician || t('Not set')}</div>
                                <div>{i[name]?.date ? moment(i[name]?.date).format(config.defaultDateTimeFormat) : ''}</div>
                            </div>
                        </div>
                    );
                })}
            </>
        );
    };

    const getNoteName = (noteType) => {
        if (noteType) {
            let text = noteType;
            if (noteType.includes('.')) {
                const arr = noteType.split('.');
                text = _.last(arr);
            }

            const exception = formNameException.get(text);
            if (exception) text = exception;

            return _.startCase(text);
        }
        return '-';
    };

    return (
        <div className={classes.root}>
            {loading &&
                <AgyleLoadingSpinner
                    label={t('Loading patient\'s timeline. Please wait, it may take a while.')}
                    spinnerSize={120}
                />
            }
            {!loading && renderRoot()}

            <AgyleDialog
                open={isNoteOpen}
                onClose={handleCloseNote}
                fullWidth
                title={t('Clinical Note')}
                disableSave
                width="xl"
                contentStyle={classes.modalBackground}
            >
                <SingleNoteComponent
                    index={0}
                    key={selectedNote?.__id}
                    note={selectedNote}
                    clinicalNoteTypes={clinicalNoteType}
                />
            </AgyleDialog>

            <AgyleDialog
                open={isExpandCliniciansOpen}
                onClose={handleCloseExpandClinicians}
                fullWidth
                title={t('Clinicians')}
                disableSave
                width="md"
                contentStyle={classes.modalBackground}
            >
                {renderExpandClinicians()}
            </AgyleDialog>
        </div>
    );
};

export default PatientTimeline;

const TabPanel = (props) => {
    const {children, value, index, ...other} = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box style={{paddingTop: 10}}>
                    {children}
                </Box>
            )}
        </div>
    );
};